import React, { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, addDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useAuthStore } from '../stores';
import { API_URL, formatCurrency } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { CalendarDays, MapPin, User, FileText, Plus } from 'lucide-react';
import 'react-big-calendar/lib/css/react-big-calendar.css';

// Setup date-fns localizer for French
const locales = { 'fr': fr };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales,
});

// Custom messages in French
const messages = {
  allDay: 'Journée',
  previous: 'Précédent',
  next: 'Suivant',
  today: "Aujourd'hui",
  month: 'Mois',
  week: 'Semaine',
  day: 'Jour',
  agenda: 'Agenda',
  date: 'Date',
  time: 'Heure',
  event: 'Événement',
  noEventsInRange: 'Aucun événement sur cette période',
  showMore: (total) => `+ ${total} autres`,
};

const EventDetailDialog = ({ event, open, onClose }) => {
  if (!event) return null;
  
  const isQuote = event.type === 'quote';
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            {isQuote ? <FileText className="w-5 h-5" /> : <CalendarDays className="w-5 h-5" />}
            {event.title}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <Badge 
            style={{ backgroundColor: event.color + '20', color: event.color, borderColor: event.color }}
            variant="outline"
          >
            {event.status === 'signed' ? 'Signé' : 
             event.status === 'accepted' ? 'Accepté' : 
             event.status === 'sent' ? 'Envoyé' :
             event.status === 'confirmed' ? 'Confirmé' : event.status}
          </Badge>
          
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2 text-muted-foreground">
              <CalendarDays className="w-4 h-4" />
              <span>
                {format(new Date(event.start), 'dd/MM/yyyy')} 
                {event.end && event.end !== event.start && ` - ${format(new Date(event.end), 'dd/MM/yyyy')}`}
              </span>
            </div>
            
            {event.client_name && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <User className="w-4 h-4" />
                <span>{event.client_name}</span>
              </div>
            )}
            
            {event.location && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <MapPin className="w-4 h-4" />
                <span>{event.location}</span>
              </div>
            )}
            
            {isQuote && event.total_ttc && (
              <div className="pt-2 border-t">
                <p className="text-lg font-bold">{formatCurrency(event.total_ttc)}</p>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const CalendarPage = () => {
  const { token } = useAuthStore();
  const navigate = useNavigate();
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState('month');
  
  // State for slot selection (creating new quote)
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const { data: calendarData = [], isLoading } = useQuery({
    queryKey: ['calendar'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/calendar`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch calendar');
      return res.json();
    },
  });

  // Transform data for react-big-calendar
  const events = useMemo(() => {
    return calendarData.map((item) => ({
      ...item,
      start: new Date(item.start),
      end: new Date(item.end),
    }));
  }, [calendarData]);

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setDialogOpen(true);
  };
  
  // Handle slot selection (click on empty date or drag to select range)
  const handleSelectSlot = useCallback(({ start, end }) => {
    // Adjust end date: if same day click, make end = start + 1 day for display
    // If multi-day selection, end is already correct
    setSelectedSlot({ start, end: end > start ? end : addDays(start, 1) });
    setCreateDialogOpen(true);
  }, []);
  //Fonction utilitaire pour éviter le bug du J-1 dû aux fuseaux horaires : on force les heures à midi (12:00) pour que la date affichée soit correcte même si l'utilisateur est dans un fuseau horaire différent
  const toSafeISOString = (date) => {
    if (!date) return null;
    const safeDate = new Date(date);
    // On force l'heure à 12:00:00 (Midi) LOCALE
    safeDate.setHours(12, 0, 0, 0); 
    return safeDate.toISOString();
};
  // Navigate to quotes page with pre-filled dates
  const handleCreateQuote = () => {
    if (selectedSlot) {
      const startStr = toSafeISOString(selectedSlot.start);
      const endStr = toSafeISOString(selectedSlot.end);
      navigate(`/quotes?start=${startStr}&end=${endStr}`);
    }
    setCreateDialogOpen(false);
    setSelectedSlot(null);
  };
  
  // Handle calendar navigation
  const handleNavigate = useCallback((newDate) => {
    setCalendarDate(newDate);
  }, []);
  
  // Handle view change
  const handleViewChange = useCallback((newView) => {
    setCalendarView(newView);
  }, []);

  // Custom event styling
  const eventStyleGetter = (event) => {
    return {
      style: {
        backgroundColor: event.color || '#3b82f6',
        borderRadius: '4px',
        opacity: 0.9,
        color: 'white',
        border: 'none',
        display: 'block',
        fontSize: '12px',
        padding: '2px 4px',
      },
    };
  };

  if (isLoading) {
    return (
      <div className="space-y-6" data-testid="calendar-loading">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-[600px] w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold">Planning</h1>
          <p className="text-muted-foreground">
            Vue calendrier des événements et devis signés
          </p>
        </div>
        
        {/* Legend */}
        <div className="flex flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#22c55e]" />
            <span className="text-sm">Événement confirmé</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#10b981]" />
            <span className="text-sm">Devis accepté</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#8b5cf6]" />
            <span className="text-sm">Devis signé</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#f59e0b]" />
            <span className="text-sm">Devis envoyé</span>
          </div>
        </div>
      </div>

      {/* Calendar */}
      <Card className="p-4">
        <div 
          className="h-[600px] calendar-wrapper"
          style={{
            '--rbc-event-bg': 'var(--primary)',
          }}
        >
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: '100%' }}
            messages={messages}
            culture="fr"
            date={calendarDate}
            view={calendarView}
            onNavigate={handleNavigate}
            onView={handleViewChange}
            onSelectEvent={handleSelectEvent}
            onSelectSlot={handleSelectSlot}
            selectable
            eventPropGetter={eventStyleGetter}
            views={['month', 'week', 'day', 'agenda']}
            defaultView="month"
            popup
          />
        </div>
      </Card>

      {/* Create Quote Dialog - triggered by slot selection */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Créer un devis
            </DialogTitle>
            <DialogDescription>
              Créer un nouveau devis pour la période sélectionnée
            </DialogDescription>
          </DialogHeader>
          
          {selectedSlot && (
            <div className="space-y-4">
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Période sélectionnée</p>
                <p className="font-medium">
                  {format(selectedSlot.start, 'dd MMMM yyyy', { locale: fr })}
                  {selectedSlot.end && selectedSlot.start.getTime() !== selectedSlot.end.getTime() && (
                    <> → {format(selectedSlot.end, 'dd MMMM yyyy', { locale: fr })}</>
                  )}
                </p>
              </div>
              
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => {
                    setCreateDialogOpen(false);
                    setSelectedSlot(null);
                  }}
                >
                  Annuler
                </Button>
                <Button 
                  className="flex-1"
                  onClick={handleCreateQuote}
                  data-testid="create-quote-from-calendar"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Créer le devis
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Event Detail Dialog */}
      <EventDetailDialog
        event={selectedEvent}
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setSelectedEvent(null);
        }}
      />

      {/* Custom styles for dark mode */}
      <style>{`
        .rbc-calendar {
          font-family: inherit;
        }
        .dark .rbc-calendar {
          color: rgb(var(--foreground));
        }
        .dark .rbc-toolbar button {
          color: rgb(var(--foreground));
          border-color: rgb(var(--border));
        }
        .dark .rbc-toolbar button:hover {
          background-color: rgb(var(--accent));
        }
        .dark .rbc-toolbar button.rbc-active {
          background-color: rgb(var(--primary));
          color: rgb(var(--primary-foreground));
        }
        .dark .rbc-month-view,
        .dark .rbc-time-view,
        .dark .rbc-agenda-view {
          background-color: rgb(var(--card));
          border-color: rgb(var(--border));
        }
        .dark .rbc-header {
          border-color: rgb(var(--border));
          color: rgb(var(--foreground));
        }
        .dark .rbc-day-bg,
        .dark .rbc-time-content {
          border-color: rgb(var(--border));
        }
        .dark .rbc-off-range-bg {
          background-color: rgb(var(--muted) / 0.3);
        }
        .dark .rbc-today {
          background-color: rgb(var(--primary) / 0.1);
        }
        .dark .rbc-date-cell {
          color: rgb(var(--foreground));
        }
        .dark .rbc-off-range {
          color: rgb(var(--muted-foreground));
        }
        .rbc-event {
          font-size: 11px !important;
          padding: 2px 4px !important;
        }
        .rbc-show-more {
          color: rgb(var(--primary));
          font-size: 11px;
        }
      `}</style>
    </div>
  );
};

export default CalendarPage;
